export enum BillingPeriod {
  MONTHLY = "month",
  ANNUAL = "year"
}

export enum SubscriptionMode {
  FREE = "free",
  PAID = "paid"
}

export enum CreditBillingStrategy {
  PER_COUNTRY = "perCountry",
  PER_USE = "perUse"
}

export enum InvoiceStatus {
  PAID = "paid",
  FAILED = "failed"
}

export interface PlanPricing {
  price: number;
  period: BillingPeriod;
  priceLabel: string;
}

interface SubscriptionBanner {
  id: string;
  title: string;
  action: {
    type: string;
    label: string;
    url?: string;
  };
  tone: "info" | "warning" | "success" | "critical";
  hideCloseIcon?: boolean;
}

interface SubscriptionPlan {
  id: string;
  type: string;
  label: string;
  description: string;
  pricing: PlanPricing[];
  isPopular: boolean;
  features: string[];
  patientLimit?: number;
}

export interface AvailableSubscriptionPlan extends SubscriptionPlan {
  isCurrent: boolean;
  cta?: {
    label: string;
  };
}

interface CurrentSubscriptionUsage {
  current: number;
  max: number;
  percent: number;
}

interface CurrentSubscription {
  active: SubscriptionPlan;
  mode: SubscriptionMode;
  billingPeriod?: BillingPeriod;
  nextBillingDate?: string;
  usage?: CurrentSubscriptionUsage;
  banner?: SubscriptionBanner;
  paymentMethod?: PaymentMethod;
}

interface PaymentMethod {
  type?: 'card';
  name: string;
  details: string;
}

interface StoragePlanPricing {
  price: number;
  period: string;
  priceLabel: string;
}

interface StoragePlan {
  id: string;
  type: string;
  label: string;
  sizeInGB: number;
  pricing: StoragePlanPricing;
}

interface CurrentStorage {
  active: StoragePlan;
  current: number;
  max: number;
  percent: number;
}

interface CountryPrice {
  price: number;
  priceLabel: string;
}

interface PerUsePrice {
  price: number;
  priceLabel: string;
}

export interface UsageRate {
  id: string;
  label: string;
  description: string;
  billingStrategy: CreditBillingStrategy;
  perCountryPrices?: {
    [countryCode: string]: CountryPrice;
  };
  perUsePrice?: PerUsePrice;
}

interface Credits {
  id: string;
  label: string;
  description: string;
  current: number;
  currentLabel: string;
  minAmount: number;
  maxAmount: number;
  usageRates: UsageRate[];
}

export interface SubscriptionData {
  currency: string;
  isSandbox: boolean;
  currentSubscription: CurrentSubscription;
  subscriptionPlans: AvailableSubscriptionPlan[];
  currentStorage: CurrentStorage;
  availableStorageAddons: StoragePlan[];
  credits: Credits;
}

export interface InvoiceData {
  dateIssued: string;
  amount: number;
  amountLabel: string;
  currency: string;
  status: InvoiceStatus;
  number: string;
  downloadLink: string;
}

// Types for CreditsCard component
export interface UsageRateItem {
  type: string;
  name: string;
  price: string;
  description: string;
  icon: string;
  availableCountries?: string[];
  selectedCountry?: string;
  hasCountrySelect?: boolean;
  ratePerCountry?: Array<{ rate: number; currency: string; countryCode: string }>;
}
